import React from 'react';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import { Box, Button, Dialog, DialogContent, Link, SuccessCircleIcon, Typography } from '@availity/element';

import DynamicField from '../../../Common/components/DynamicField';

import IncidentStore from '../../../Common/stores/IncidentStore';
import CommonStore from '../../../Common/stores/CommonStore';
import ProviderStore from '../../../Common/stores/ProviderStore';

const ContactAndHome = ({
  text,
  emails,
  homeLabel,
  onHome,
}) => (
  <>
    <Typography sx={{ textAlign: 'center', margin: '1.25rem' }}>
      {text}{' '}
      {emails}
    </Typography>
    <Box>
      <Button
        data-id="home-button"
        variant="contained"
        type="button"
        color="primary"
        onClick={onHome}
      >
        {homeLabel}
      </Button>
    </Box>
  </>
);

ContactAndHome.propTypes = {
  text: PropTypes.node.isRequired,
  emails: PropTypes.node,          
  homeLabel: PropTypes.node.isRequired,
  onHome: PropTypes.func.isRequired,
};

ContactAndHome.defaultProps = {
  emails: null,
};

const ConfirmationModal = ({ isOpen, incidentStore, commonStore, providerStore }) => {
  const navigate = useNavigate();
  const { confirmationId } = incidentStore;
  const { inPublicfacingFlow } = commonStore;

  const navigateToLandingPage = () => {
    incidentStore.setOpenConfirmationModal();
    providerStore.resetStore();
    navigate('/', { replace: true });
  };

  const handleNavigation = () => {
    incidentStore.setOpenConfirmationModal();
    if (inPublicfacingFlow) {
      navigate('/', { replace: true });
    } else {
      window.location.href = `/web/spc/spaces/#/${commonStore.brand.spaceId}`;
    }
  };

  return (
    <Dialog
      open={isOpen}
      id="confirmation-modal"
      scroll="body"
      disableEscapeKeyDown
      disableBackdrop
      Click
      closeButton={false}
      sx={{
        '.css-1uv3rse-MuiPaper-root-MuiDialog-paper': {
          maxWidth: '45rem',
        },
      }}
    >
      <DialogContent id="confirmation-modal-body" sx={{ padding: '1.875rem' }}>
        <Box
          id="confirmation-modal-content-box"
          sx={{ mt: '1.25rem', mb: '1.25rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
        >
          <SuccessCircleIcon id="success-icon" fontSize="large" color="success" sx={{ mb: '2.5rem' }} />
          <DynamicField fieldName="submissionTextWrapperROI">
            <DynamicField fieldName="confirmationHeaderFlavor1">
              <Typography id="confirmation-text" variant="h2" sx={{ textAlign: 'center', margin: '0.5rem' }}>
                Incident Submitted
              </Typography>
            </DynamicField>
            <DynamicField fieldName="confirmationHeaderFlavor2">
              <Typography id="confirmation-text" variant="h2" sx={{ textAlign: 'center', margin: '0.5rem' }}>
                Referral Submitted
              </Typography>
            </DynamicField>
            <DynamicField fieldName="submissionTextNCAndMO">
              <Typography id="confirmation-text" sx={{ textAlign: 'center', margin: '0 0 1rem' }}>
                Thank you for completing the Quality of Care Report.
              </Typography>
            </DynamicField>
            <DynamicField fieldName="submissionTextWI">
              <Typography id="confirmation-text-wi" sx={{ textAlign: 'center', margin: '0 0 1rem' }}>
                Thank you for completing the Incident Report.
              </Typography>
            </DynamicField>
          </DynamicField>
          <DynamicField fieldName="submissionTextWrapperRMIR">
            <Typography id="confirmation-text-rmir" sx={{ textAlign: 'center', margin: '0 0 1rem' }}>
              Thank you for completing the Risk Management Report.
            </Typography>
          </DynamicField>

          <DynamicField fieldName="confirmationIDROI">
            <Typography id="confirmation-id" sx={{ fontWeight: 'bold', margin: '1.25rem' }}>
              Confirmation: {confirmationId || ''}
            </Typography>
          </DynamicField>
          <DynamicField fieldName="confirmationIDRMIR">
            <Typography id="confirmation-id-rmir" sx={{ fontWeight: 'bold', margin: '1.25rem' }}>
              Confirmation number is: {confirmationId || ''}
            </Typography>
          </DynamicField>
          <DynamicField fieldName="thanksTextOHAndKS">
            <Typography id="description-text" sx={{ textAlign: 'center', margin: '1.25rem' }}>
              Thank you for reporting this incident. We will investigate within 24 hours. You may be contacted if we
              have questions.
            </Typography>
            <Button
              id="create-another-incident"
              onClick={(e) => {
                e.preventDefault(); // Prevent default form submission
                navigateToLandingPage(); // Explicitly call the submit handler
              }}
              variant="contained"
              color="primary"
            >
              Report Another Incident
            </Button>
          </DynamicField>
          <DynamicField fieldName="thanksTextNCAndMO">
            <Typography id="description-text" sx={{ textAlign: 'center', margin: '1.25rem' }}>
              If you have questions regarding this form or need to update a form you’ve previously submitted, please
              contact our Quality Management Department at{' '}
              <DynamicField fieldName="mailForNEMarket">
                <Link id="mail-to-NE" href="mailto:QualityManagementPQOCTeam@anthem.com">
                  QualityManagementPQOCTeam@anthem.com
                </Link>
              </DynamicField>
              <DynamicField fieldName="mailForMOMarket">
                <Link id="mail-to-MO" href="mailto:MO.HPGA@anthem.com">
                  MO.HPGA@anthem.com
                </Link>
              </DynamicField>
              <DynamicField fieldName="mailForNCMarket">
                <Link id="mail-to-NC" href="mailto:Qualityofcare@healthybluenc.com">
                  Qualityofcare@healthybluenc.com
                </Link>
              </DynamicField>
              .
            </Typography>
            <Box>
              <Button
                data-id="home-button"
                id="home-button-link"
                variant="contained"
                type="button"
                color="primary"
                onClick={handleNavigation}
              >
                <DynamicField fieldName="mailForNEMarket">Healthy Blue NE Home</DynamicField>
                <DynamicField fieldName="mailForMOMarket">Healthy Blue MO Home</DynamicField>
                <DynamicField fieldName="mailForNCMarket">Healthy Blue NC Home</DynamicField>
              </Button>
            </Box>
          </DynamicField>
          <DynamicField fieldName="thanksTextCO">
            <ContactAndHome
              onHome={handleNavigation}
              text={
                <>
                  Thank you for reporting this incident. We will begin our investigation and may contact you if we have questions.
                </>
              }
             homeLabel={<DynamicField fieldName="mailForCOMarket">Colorado Community Health Alliance Home</DynamicField>}
            />
          </DynamicField>
          <DynamicField fieldName="thanksTextWI">
            <Typography id="description-text-wi" sx={{ textAlign: 'center', margin: '1.25rem' }}>
              If you have questions regarding this form or need to update a form you&apos;ve previously submitted, please
              contact our Quality Management Department at{' '}
              <DynamicField fieldName="mailForWIMarket">
                <Link id="mail-to-WI" href="mailto:WI_Incidents@anthem.com">
                  WI_Incidents@anthem.com
                </Link>
              </DynamicField>
              .
            </Typography>
            {!inPublicfacingFlow && (
              <Box>
                <Button
                  data-id="home-button"
                  id="home-button-link-wi"
                  variant="contained"
                  type="button"
                  color="primary"
                  onClick={handleNavigation}
                >
                  <DynamicField fieldName="mailForWIMarket">ABCBS Home Page</DynamicField>
                </Button>
              </Box>
            )}
          </DynamicField>
          <DynamicField fieldName="anyQuestionRMIR">
            <Typography id="description-text-rmir" sx={{ textAlign: 'center', margin: '1.25rem' }}>
              If you have questions regarding the form or need to update a previously submitted form, please contact
              Risk Management at (813-523-0992 or 305.335.8420).
            </Typography>
            <Box>
              <Button
                data-id="home-button"
                id="home-button-link"
                variant="contained"
                type="button"
                color="secondary"
                onClick={handleNavigation}
              >
                Close
              </Button>
            </Box>
          </DynamicField>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

ConfirmationModal.propTypes = {
  isOpen: PropTypes.bool,
  incidentStore: PropTypes.oneOf([IncidentStore]).isRequired,
  commonStore: PropTypes.oneOf([CommonStore]).isRequired,
  providerStore: PropTypes.oneOf([ProviderStore]).isRequired,
};

export default inject('incidentStore', 'commonStore', 'providerStore')(observer(ConfirmationModal));
